import { Pipe, PipeTransform } from '@angular/core';

/**
 * A custom Angular pipe that determines if any object in a given array has a specific property
 * equal to a provided value.
 *
 * The `SomeByPropertyPipe` is a utility pipe that takes an array, a property key, and a value.
 * It performs a search to verify if at least one object in the array has the specified property
 * that matches the provided value.
 *
 * This pipe is particularly useful in Angular templates to conditionally display content based on
 * dynamic data checks.
 *
 * @example
 * ```html
 * <div *ngIf="users | someByProperty: 'isActive': true">
 *   At least one user is active.
 * </div>
 * ```
 *
 * @example
 * ```typescript
 * const users = [
 *   { id: 1, isActive: false },
 *   { id: 2, isActive: true },
 * ];
 * const result = someByPropertyPipe.transform(users, 'isActive', true); // true
 * ```
 *
 * @category Pipes
 */
@Pipe({
  name: 'someByProperty',
  standalone: true,
})
export class SomeByPropertyPipe implements PipeTransform {
  transform<T>(array: T[] | null | undefined, property: keyof T, value: unknown): boolean {
    if (!array || array.length === 0) {
      return false;
    }
    return array.some((item) => item[property] === value);
  }
}
